import type { MDXEditor } from '@mdxeditor/editor';
import type { DefaultValues } from 'react-hook-form';
import { useCallback, useEffect, useRef } from 'react';
import { filterUndefineds } from '@olivierpascal/helpers';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IFileUploaderValue } from '~/components/organisms/fields/FileUploader';
import type { IContainerProps } from '~/components/utils/Container';
import type { IAny, IFormMode, IMaybeAsync } from '~/helpers/types';
import type { IApiGetArticleResponseBody } from '~/lib/api/modules/Articles';
import { FormBadgesSection } from '~/components/formSections/FormBadgesSection';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { useProcessedDocuments } from '~/hooks/useProcessedDocuments';
import { useProcessedPictures } from '~/hooks/useProcessedPictures';
import { useProcessedVideos } from '~/hooks/useProcessedVideos';
import { useCategoriesQuery } from '~/lib/api/modules/Categories/hooks/useCategoriesQuery';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';
import { ArticleFormActionButtonSection } from './ArticleFormActionButtonSection';
import { ArticleFormAdvancedOptionsSection } from './ArticleFormAdvancedOptionsSection';
import { ArticleFormDocumentsSection } from './ArticleFormDocumentsSection';
import { ArticleFormEventSection } from './ArticleFormEventSection';
import { ArticleFormGallerySection } from './ArticleFormGallerySection';
import { ArticleFormMainMediaSection } from './ArticleFormMainMediaSection';
import { ArticleFormMainSection } from './ArticleFormMainSection';
import { ArticleFormMonetizationSection } from './ArticleFormMonetizationSection';
import { ArticleFormPublicationSection } from './ArticleFormPublicationSection';
import { ArticleFormSubmitButtons } from './ArticleFormSubmitButtons';
import { ArticleFormUnpublicationSection } from './ArticleFormUnpublicationSection';

// TODO: focus mode (https://crowdaa.atlassian.net/browse/DV2-208)

export type IArticleFormValues = {
  title?: string;
  markdown?: string;
  html?: string;
  categoryIds?: Array<string>;
  mainMedias?: Array<IFileUploaderValue>;
  mainMediaCaption?: string;
  actionButtonEnabled?: boolean;
  actionButtonType?: string;
  actionButtonPrefix?: string;
  actionButtonTarget?: string;
  actionButtonTargetCategoryId?: string;
  actionButtonLabel?: string;
  galleryEnabled?: boolean;
  galleryPictures?: Array<IFileUploaderValue>;
  feedPictures?: Array<IFileUploaderValue>;
  documentsEnabled?: boolean;
  documents?: Array<IFileUploaderValue>;
  documentsButtonLabel?: string;
  userBadges?: Array<string>;
  requireAllUserBadges?: boolean;
  hideComments?: boolean;
  hideShareButtons?: boolean;
  rawContent?: boolean;
  pinned?: boolean;
  publicationScheduled?: boolean;
  publishAt?: string;
  unpublicationScheduled?: boolean;
  unpublishAt?: string;
  isPublished?: boolean;
  notificationTitle?: string;
  notificationBody?: string;
  productId?: string;
  isEvent?: boolean;
  eventStartDate?: string;
  eventEndDate?: string;
  fullscreen?: boolean;
  hideFromFeed?: boolean;
  type?: 'webview' | 'article' | 'poll';
};

export type IArticleFormSubmitAction =
  | 'saveDraft'
  | 'publish'
  | 'publishAndNotify'
  | 'unpublish'
  | 'unpublishAndNotify';

export type IArticleFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IArticleFormValues>,
    | 'form'
    | 'children'
    | 'onSubmit'
    | 'submitting'
    | 'submitAction'
    | 'submitButtonLabel'
  > & {
    mode: IFormMode;
    appId: string;
    defaultValues?: DefaultValues<IArticleFormValues>;
    onSubmit?: (
      values: IArticleFormValues,
      event?: React.BaseSyntheticEvent,
      action?: IArticleFormSubmitAction,
    ) => IMaybeAsync<IAny>;
    onArticleChange?: (article: IApiGetArticleResponseBody) => void;
    submittingAction?: IArticleFormSubmitAction;
    canSchedulePublication?: boolean;
  };

export const ArticleForm: React.FC<IArticleFormProps> = (props) => {
  const {
    sx,
    mode,
    appId,
    defaultValues,
    onSubmit,
    onArticleChange,
    submittingAction,
    canSchedulePublication,
    ...other
  } = props;

  const previewEnabled = !!onArticleChange;

  const markdownEditorRef = useRef<React.ElementRef<typeof MDXEditor>>(null);
  const onRecoverField = useCallback((fieldName: string, value: unknown) => {
    if (fieldName === 'markdown') {
      markdownEditorRef.current?.setMarkdown(value as string);

      return true;
    }

    return false;
  }, []);

  const form = usePersistentForm<IArticleFormValues>({
    defaultValues,
    onRecoverField,
  });
  useFormAutoFocus({ form, name: 'title' });

  const categoriesQuery = useCategoriesQuery(
    { appId },
    { enabled: previewEnabled },
  );
  const userQuery = useUserQuery({});
  const username =
    userQuery.data?.profile?.username ?? userQuery.data?.username;

  const mainPictureIds = form
    .watch('mainMedias')
    ?.filter(({ type }) => type === 'picture')
    .map(({ id }) => id);
  const galleryPictureIds = form.watch('galleryPictures')?.map(({ id }) => id);
  const pictureIds = [...(mainPictureIds ?? []), ...(galleryPictureIds ?? [])];
  const processedPictures = useProcessedPictures({
    appId,
    ids: pictureIds,
    enabled: previewEnabled,
  });

  const mainVideoIds = form
    .watch('mainMedias')
    ?.filter(({ type }) => type === 'video')
    .map(({ id }) => id);
  const videoIds = mainVideoIds ?? [];
  const processedVideos = useProcessedVideos({
    appId,
    ids: videoIds,
    enabled: previewEnabled,
  });

  const documentIds = form.watch('documents')?.map(({ id }) => id) ?? [];
  const processedDocuments = useProcessedDocuments({
    appId,
    ids: documentIds,
    enabled: previewEnabled,
  });

  const handleArticleChange = useCallback((): void => {
    const values = form.getValues();

    const categories = values.categoryIds
      ? filterUndefineds(
          values.categoryIds.map((categoryId) =>
            categoriesQuery.data?.categories.find(
              (category) => category._id === categoryId,
            ),
          ),
        )
      : [];
    const pictures = processedPictures.data.length
      ? // Make sure processedPictures.data and form values are synced.
        processedPictures.data.filter(
          ({ _id }) =>
            values.mainMedias?.map(({ id }) => id).includes(_id) ||
            values.galleryPictures?.map(({ id }) => id).includes(_id),
        )
      : undefined;
    const videos = processedVideos.data.length
      ? // Make sure processedVideos.data and form values are synced.
        processedVideos.data.filter(({ _id }) =>
          values.mainMedias?.map(({ id }) => id).includes(_id),
        )
      : undefined;
    const pdfs = processedDocuments.data.length
      ? // Make sure processedDocuments.data and form values are synced.
        processedDocuments.data
          .map(({ _id }) => _id)
          .filter((id) => values.documents?.map(({ id }) => id).includes(id))
      : undefined;
    const actions = values.actionButtonEnabled
      ? [
          {
            title: values.actionButtonLabel ?? '',
            url: `${values.actionButtonPrefix ?? ''}${values.actionButtonTarget ?? ''}`,
          },
        ]
      : undefined;
    const badges = values.userBadges?.length
      ? ({
          list: values.userBadges.map((id) => ({ id })),
          allow: values.requireAllUserBadges ? 'all' : 'any',
        } satisfies IApiGetArticleResponseBody['badges'])
      : undefined;

    const article: IApiGetArticleResponseBody = {
      _id: '__fake_id',
      appId,
      createdAt: new Date().toISOString(),
      title: values.title,
      text: values.type === 'article' ? values.html : values.markdown,
      categories,
      pictures,
      videos,
      pdfs,
      pdfsOpenButton: values.documentsButtonLabel,
      mediaCaptions: values.mainMediaCaption,
      actions,
      isWebview: values.type === 'webview',
      badges,
      views: 42397,
      likes: 179,
      commentsCount: 31,
      pinned: values.pinned,
      user: { username },
      storeProductId: values.productId,
      isEvent: values.isEvent,
      eventStartDate: values.eventStartDate,
      eventEndDate: values.eventEndDate,
      publicationDate: values.publishAt,
      hideFromFeed: values.hideFromFeed,
      displayOptions: {
        hideComments: values.hideComments,
        hideSocial: values.hideShareButtons,
        fullscreen: values.fullscreen,
        rawContent: values.rawContent,
      },
      isPoll: values.type === 'poll',
    };

    onArticleChange?.(article);
  }, [
    appId,
    form,
    onArticleChange,
    categoriesQuery.data?.categories,
    processedPictures.data,
    processedVideos.data,
    processedDocuments.data,
    username,
  ]);

  useEffect(() => {
    // Trigger the article change event when form values change.
    if (!previewEnabled) {
      return;
    }

    const subscription = form.watch(() => {
      // Workaround
      // See: https://github.com/react-hook-form/react-hook-form/issues/11734
      setTimeout(() => handleArticleChange(), 0);
    });

    return () => subscription.unsubscribe();
  }, [form, previewEnabled, handleArticleChange]);

  useEffect(() => {
    // Trigger the article change event when the loaded pictures change.
    if (!previewEnabled) {
      return;
    }

    handleArticleChange();
  }, [previewEnabled, handleArticleChange, processedPictures.data]);

  const isPublished = form.watch('isPublished');
  const publicationScheduled = form.watch('publicationScheduled');

  return (
    <ContentBlock.FormBody
      sx={sx}
      form={form}
      submitAction={
        <ArticleFormSubmitButtons
          mode={mode}
          form={form}
          published={isPublished}
          publicationScheduled={publicationScheduled}
          onSubmit={onSubmit}
          submittingAction={submittingAction}
        />
      }
      actionBarAlwaysVisible
      {...other}
    >
      <ArticleFormMainSection
        mode={mode}
        form={form}
        appId={appId}
        markdownEditorRef={markdownEditorRef}
        defaultMarkdown={defaultValues?.markdown}
      />
      <ArticleFormMainMediaSection mode={mode} form={form} appId={appId} />
      {form.watch('type') === 'article' && (
        <>
          <ArticleFormActionButtonSection
            mode={mode}
            form={form}
            appId={appId}
          />
          <ArticleFormGallerySection mode={mode} form={form} appId={appId} />
          <ArticleFormDocumentsSection mode={mode} form={form} appId={appId} />
        </>
      )}
      <ArticleFormPublicationSection mode={mode} form={form} />
      <ArticleFormUnpublicationSection
        mode={mode}
        form={form}
        canSchedulePublication={canSchedulePublication || true}
      />
      <ArticleFormEventSection mode={mode} form={form} />
      <ArticleFormMonetizationSection mode={mode} form={form} appId={appId} />
      <FormBadgesSection mode={mode} form={form} appId={appId} />
      <ArticleFormAdvancedOptionsSection mode={mode} form={form} />
    </ContentBlock.FormBody>
  );
};
